import { useCallback, useEffect, useState } from "react";
import { Loader2, Download, PlugZap, CalendarCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi, opsDownload, money } from "./lib/api";

const TONE = {
  accruing: "border-slate-200 bg-slate-50 text-slate-600",
  fully_billed_except_retention: "border-amber-200 bg-amber-50 text-amber-700",
  follow_up_active: "border-rose-200 bg-rose-50 text-rose-700",
  eligible_date_set: "border-sky-200 bg-sky-50 text-sky-700",
  retention_billed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

/** FIN-6 — retention. The report is plain on purpose. The value is the follow-up: a job billed
 *  in full apart from retention, with nobody chasing the date, is money left on the table. */
export default function RetentionPage() {
  const [data, setData] = useState(null);
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await opsApi.retention()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load retention"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setEligible = async (row) => {
    const value = dates[row.project_id];
    if (!value) return;
    setBusy(true);
    try {
      await opsApi.retentionEligibleDate(row.project_id, { eligible_date: value,
                                                           source: "GC confirmed" });
      toast.success("Eligible date recorded — the generic chase stops here");
      setDates((p) => ({ ...p, [row.project_id]: "" }));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const draft = async (row) => {
    setBusy(true);
    try {
      const r = await opsApi.retentionDraftRelease(row.project_id);
      toast.success(r.already ? `${r.pay_application.number} is already drafted`
        : `${r.pay_application.number} drafted`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be drafted"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading retention…
    </div>;
  }
  const s = data.summary;

  return (
    <div className="page-body space-y-5" data-testid="retention-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Retention outstanding", money(s.outstanding), "ret-kpi-outstanding"],
          ["Jobs holding it", `${s.projects}`, "ret-kpi-projects"],
          ["Chasing a date", `${s.follow_up_active}`, "ret-kpi-chasing"],
          ["Date confirmed", `${s.eligible_date_set}`, "ret-kpi-dated"],
          ["Oldest", `${s.oldest_days} days`, "ret-kpi-oldest"]].map(([l, v, tid]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-white p-4"
               data-testid={tid}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{l}</p>
            <p className="stat-num text-2xl font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {data.by_gc.slice(0, 6).map((g) => (
          <div key={g.gc} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <p className="text-xs font-medium text-slate-800">{g.gc}</p>
            <p className="text-xs text-slate-500">
              {money(g.amount)} · {g.count} job{g.count === 1 ? "" : "s"} · oldest {g.oldest}d
            </p>
          </div>
        ))}
        <Button variant="outline" size="sm" className="ml-auto" data-testid="retention-export"
                onClick={() => opsDownload("/retention/export", "retention-outstanding.csv")
                  .catch(() => toast.error("That export could not be built"))}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> Export
        </Button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
           data-testid="retention-not-wired">
        <p className="flex items-center gap-2 text-xs font-semibold text-amber-900">
          <PlugZap className="h-3.5 w-3.5" /> Not connected yet
        </p>
        {data.not_wired.map((n, i) => (
          <p key={i} className="text-xs text-amber-900">· {n}</p>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white"
           data-testid="retention-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Job</th>
              <th className="px-3 py-2 text-left">GC</th>
              <th className="px-3 py-2 text-right">%</th>
              <th className="px-3 py-2 text-right">Withheld</th>
              <th className="px-3 py-2 text-right">Billed</th>
              <th className="px-3 py-2 text-right">Remaining</th>
              <th className="px-3 py-2 text-left">Final billing</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Eligible date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.project_id} data-testid={`retention-row-${r.job_number || r.project_id}`}
                  className={r.state === "follow_up_active" ? "bg-rose-50/50" : ""}>
                <td className="px-3 py-2 text-xs">
                  <span className="font-mono text-slate-500">{r.job_number || "—"}</span>
                  <p className="max-w-[13rem] truncate font-medium text-slate-900">
                    {r.project_name}
                  </p>
                </td>
                <td className="max-w-[9rem] truncate px-3 py-2 text-xs text-slate-600">
                  {r.gc || "—"}
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  {r.retention_percent ? `${r.retention_percent}%` : "—"}
                </td>
                <td className="px-3 py-2 text-right text-xs">{money(r.withheld_to_date)}</td>
                <td className="px-3 py-2 text-right text-xs">{money(r.retention_billed)}</td>
                <td className="px-3 py-2 text-right text-xs font-semibold">
                  {money(r.retention_remaining)}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {r.final_billing_date || "—"}
                  {r.age_days ? <span className="text-slate-400"> · {r.age_days}d</span> : null}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${
                    TONE[r.state]}`}>{r.state_label}</span>
                  {r.requests_sent ? (
                    <p className="text-[11px] text-slate-400">
                      {r.requests_sent} asked · next {r.next_request || "—"}
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  {r.eligible_date ? (
                    <div className="text-xs">
                      <p className="flex items-center gap-1 font-medium text-sky-700">
                        <CalendarCheck className="h-3 w-3" /> {r.eligible_date}
                      </p>
                      {r.release_drafted_id ? (
                        <p className="text-[11px] text-emerald-700">release drafted</p>
                      ) : (
                        <Button size="sm" variant="outline" className="mt-1 h-6 text-[11px]"
                                disabled={busy} data-testid={`retention-draft-${r.job_number}`}
                                onClick={() => draft(r)}>
                          <FileText className="mr-1 h-3 w-3" /> Draft release
                        </Button>
                      )}
                    </div>
                  ) : r.retention_remaining > 0 ? (
                    <div className="flex items-center gap-1">
                      <Input type="date" className="h-7 w-32 text-[11px]"
                             data-testid={`retention-date-${r.job_number}`}
                             value={dates[r.project_id] || ""}
                             onChange={(e) => setDates((p) => ({ ...p,
                                                                 [r.project_id]: e.target.value }))} />
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                              disabled={busy || !dates[r.project_id]}
                              data-testid={`retention-save-${r.job_number}`}
                              onClick={() => setEligible(r)}>Set</Button>
                    </div>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500">
                No retention is being held yet. It appears as soon as a job bills.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
